import type { ZoomBookingMember } from '@nrx/booking-ui';
import { Box, Heading, Separator, Text } from '@nrx/ui';
import type { JSX } from 'react';

import { ConsultBookingFrame } from '../one-to-one/consult-booking-frame.js';

type InitialConsultBlockProps = {
  scheduleUrl: string;
  member: ZoomBookingMember;
  consultType: string;
  /** Divider below the block, drawn only when the follow-up block follows it. */
  showDivider: boolean;
};

/**
 * Fee-for-service `initial` stage: the 60-minute first consult and its embedded Zoom Scheduler.
 *
 * @remarks Only rendered when the consultants response carried an `initialConsultScheduleUrl`,
 * and suppressed for `consultStage: 'follow-up'` — see `oneToOneSubsections`.
 *
 * The rule between this block and the follow-up block is `Separator size="3"`, replacing a
 * `<div aria-hidden>` styled with a border utility (W1). It renders `<hr role="separator">`, which
 * is the correct semantics for a thematic break between two booking blocks; no spec queries
 * separators, so nothing is vacated by the added role.
 */
export function InitialConsultBlock({
  scheduleUrl,
  member,
  consultType,
  showDivider,
}: InitialConsultBlockProps): JSX.Element {
  return (
    <>
      <Box className="consult-initial-block">
        <Heading as="h3" size="6" className="consult-section-title consult-section-title--tight">
          <span className="consult-accent">Schedule an Initial Consult</span>
        </Heading>
        <Text as="p" color="muted" className="consult-initial-block__lede">
          A 60 minute initial phone consultation with a nutrition coach from NourishedRx to discuss
          how to optimize your health and wellbeing.
        </Text>
        <ConsultBookingFrame
          key={scheduleUrl}
          bookingUrl={scheduleUrl}
          member={member}
          consultType={consultType}
          title="Schedule your initial consult"
        />
      </Box>
      {showDivider ? <Separator size="3" /> : null}
    </>
  );
}
